'use client';

import { Box, Button, Flex, Input, Typography } from '@coolblue-development/freddy';
import selectFilterTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/selectFilterTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFormatNumber } from '@coolblue-development/next-formatting-utils/client';
import { convertFilterValue } from '@features/filter/components/Filters/helpers/convertFilterValue';
import { getDisplayValue } from '@features/filter/components/Filters/helpers/getDisplayValue';
import { useFacetValue } from '@features/filter/hooks/useFacetValue';
import { useTranslation } from '@i18n/useTranslation';
import type { EventFor } from '@utils/type-utils';
import { useContext, useState } from 'react';
import { coerce } from 'zod';

import { FilterPageContext } from '../../../context';
import type { InputFacet } from '../../../types';

const calculationValue = 0.005;

export default function InputFacet({ facet }: { facet: InputFacet }) {
  const { t } = useTranslation('webshop');
  const { getFilter, setFilter, trackingSubFeature } = useContext(FilterPageContext);
  const { factor, trackingValue, name, preferredUnit, displayName } = facet;
  const initialFacetValues = getFilter?.(name)?.values;
  const [currentFacetValue, setCurrentFacetValue] = useFacetValue(initialFacetValues, ['']);
  const [error, setError] = useState('');
  const validationMessageId = `${name}-error`;
  const minValue = convertFilterValue(facet.minValue);
  const maxValue = convertFilterValue(facet.maxValue);
  const minValueFormatted = useFormatNumber(minValue);
  const maxValueFormatted = useFormatNumber(maxValue);

  const { trackEvent } = useAnalytics();

  function getPlaceholderValue() {
    const averageValue = (facet.maxValue + facet.minValue) / 2;

    return String(Math.floor(averageValue / factor));
  }

  function filterFacetValue(inputValue: string) {
    const valueNumber = parseFloat(inputValue) * factor;
    if (isNaN(valueNumber)) {
      return;
    }
    const minValueToFilter = String((valueNumber - calculationValue).toFixed(5));
    const maxValueToFilter = String(valueNumber.toFixed(5));
    const convertedValue = `${minValueToFilter}-${maxValueToFilter}`;

    handleChange([convertedValue]);
  }

  function stripPresetValues(inputValue: string) {
    if (facet.values.some(value => value.value === inputValue)) {
      return '';
    }

    return getDisplayValue(inputValue);
  }

  function handleChange(newValue: string[]) {
    setCurrentFacetValue(newValue);
    setFilter?.({
      name,
      values: newValue
    });
  }

  function onClick() {
    handleSubmit(String(currentFacetValue) || '');
  }

  function validateInputValue(inputValue: string) {
    if (!inputValue) {
      setError('');
      return false;
    }

    const validationSchema = coerce
      .number()
      .min(
        minValue,
        t('filterPage.facets.minValidationError', {
          min: minValueFormatted,
          unit: preferredUnit
        })
      )
      .max(
        maxValue,
        t('filterPage.facets.maxValidationError', {
          max: maxValueFormatted,
          unit: preferredUnit
        })
      );
    const result = validationSchema.safeParse(inputValue);

    if (result.success) {
      setError('');
    } else {
      setError(result.error.errors[0].message);
    }

    return result.success;
  }

  function onKeyDown(event: EventFor<'input', 'onKeyDown'>) {
    if (event.key === 'Enter') {
      handleSubmit(event.currentTarget.value);
    }
  }

  function handleSubmit(inputValue: string) {
    if (!validateInputValue(inputValue) || !trackingValue) {
      return;
    }
    trackEvent(
      selectFilterTrigger({
        sub_feature: trackingSubFeature,
        filter_name: trackingValue,
        filter_value: inputValue.trim(),
        number_of_items: 1,
        is_recommended: false
      })
    );
    filterFacetValue(inputValue);
  }

  function onBlur() {
    validateInputValue(String(currentFacetValue?.[0] || ''));
  }

  function updateFilterValue(event: EventFor<'input', 'onChange'>) {
    setCurrentFacetValue([event.target.value.trim()]);
  }

  return (
    <Flex direction='column' mt={300} gap={100}>
      <Flex gap={200}>
        <Box sx={{ width: '10rem' }}>
          <Input
            type='number'
            name={name}
            id={name}
            endAdornment={preferredUnit}
            aria-label={displayName}
            onKeyDown={onKeyDown}
            value={stripPresetValues(String(currentFacetValue))}
            onChange={updateFilterValue}
            min={minValue}
            max={maxValue}
            placeholder={getPlaceholderValue()}
            onBlur={onBlur}
            validation={error ? 'error' : undefined}
            aria-describedby={error ? validationMessageId : undefined}
          />
        </Box>
        <Button
          variant='secondary'
          aria-label={t('filterPage.facets.ariaLabel', { displayName })}
          size='small'
          onClick={onClick}
        >
          <ArrowRight />
        </Button>
      </Flex>
      <Typography component='span' color='additional'>
        {t('filterPage.facets.minMaxHelperText', {
          min: minValueFormatted,
          max: maxValueFormatted,
          preferredUnit
        })}
      </Typography>
      {error && (
        <Typography component='span' color='sentimentDanger' id={validationMessageId}>
          {error}
        </Typography>
      )}
    </Flex>
  );
}
